import React, { useState, useEffect } from "react";
import config from "../../config";
import { requireUserId } from './userSession';
import { toast } from 'react-toastify';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Zoom,
  Tabs,
  Tab,
} from "@mui/material";
import { motion } from "framer-motion";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";
export default function PettyCashManagement() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");
  // "Hotel" | "Restaurant" — which petty-cash pool we're viewing/editing.
  const [section, setSection] = useState("Hotel");
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;
  const rowsPerPage = 6;
  const token = sessionStorage.getItem("token");
  const fetchReport = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // Hotel petty cash → BookingLadgers; Restaurant → Orderledger (order + manual cash).
      const endpoint =
        section === "Restaurant"
          ? `/home/GetRestaurantCashReport/${encodeURIComponent(company?.id)}`
          : `/home/Getcashreports/${encodeURIComponent(company?.id)}`;
      const res = await fetch(`${config.API_BASE_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setReport(data);
      console.log(data);
    } catch (err) {
      console.error("Error fetching:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const handleSubmit = async () => {
    debugger;
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    // Never write a cash transaction row with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    const payload = {
      Type: modalType === "in" ? "CR" : "DR",
      Amount: parseFloat(amount),
      Remark: remark,
      Companyid: company?.id,
      Userid: uid,
      Section: section, // "Hotel" or "Restaurant"
    };
    setSaving(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/home/SaveCashTransaction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.message == "Update successfully") {
        toast.success("Transaction saved successfully!");
        setOpenModal(false);
        setAmount("");
        setRemark("");
        fetchReport(); // refresh the current (Hotel/Restaurant) tab
      } else {
        toast.error(result.message || "Failed to save transaction.");
      }
    } catch {
      toast.error("Error saving transaction.");
    } finally {
      setSaving(false);
    }
  };

  // 🔹 Export filtered data to Excel
  const exportToExcel = () => {
    if (!filteredEntries || filteredEntries.length === 0) {
      toast.error("No records available to export!");
      return;
    }

    const formattedData = filteredEntries.map((entry) => ({
      Refno: "Ref_" +entry.id,
      Date: new Date(entry.tranjectiondate).toLocaleString('en-GB'),
      Type: entry.type,
      Amount: entry.amount.toFixed(2),
      Remark: entry.remark,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PettyCashReport");

    XLSX.writeFile(workbook, "PettyCashReport.xlsx");
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );

if (!report) return <Typography>No data available</Typography>;

const filteredEntries = report?.todayEntries?.filter((entry) => {
  const entryDate = new Date(entry.tranjectiondate);

  const matchesSearch = Object.values(entry).some((val) =>
    val?.toString().toLowerCase().includes(searchText.toLowerCase())
  );

  const matchesFromDate = fromDate
    ? entryDate >= new Date(fromDate)
    : true;

  const matchesToDate = toDate
    ? entryDate <= new Date(toDate + "T23:59:59")
    : true;

  return matchesSearch && matchesFromDate && matchesToDate;
}) || [];


  return (
    <Box sx={{ p: 4, background: "transparent" }}>
      <Typography variant="h4" fontWeight="bold" mb={2} sx={{ color: "#0a2540" }}>
        💰 Petty Cash Management
      </Typography>

      {/* Hotel vs Restaurant petty cash — separate pools */}
      <Tabs
        value={section}
        onChange={(e, v) => setSection(v)}
        sx={{ mb: 3 }}>
        <Tab label="🏨 Hotel" value="Hotel" />
        <Tab label="🍽️ Restaurant" value="Restaurant" />
      </Tabs>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        {[
          { label: "Opening Balance", value: report.openingBalance, color: "#2e7d32" },
          { label: "Today Credit", value: report.todayCredit, color: "#1e88e5" },
          { label: "Today Debit", value: report.todayDebit, color: "#d32f2f" },
          { label: "Closing Balance", value: report.closingBalance, color: "#2e7d32" },
        ].map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <motion.div whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 300 }}>
              <Paper sx={{ p: 3, borderRadius: 4, textAlign: "center" }}>
                <Typography variant="body1" color="text.secondary">
                  {item.label}
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: item.color }}>
                 {Companycurrency}{item.value?.toFixed(2)}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Search + Action Buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
  
  {/* 🔍 Search + Date Filters */}
  <Box display="flex" gap={2} alignItems="center">
    <TextField
      label="🔍 Search"
      size="small"
      value={searchText}
      onChange={(e) => setSearchText(e.target.value)}
      sx={{ width: 200 }}
    />

    <TextField
      label="From Date"
      type="date"
      size="small"
      InputLabelProps={{ shrink: true }}
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
    />

    <TextField
      label="To Date"
      type="date"
      size="small"
      InputLabelProps={{ shrink: true }}
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
    />
  </Box>

  {/* 🔘 Buttons */}
  <Box display="flex" gap={2}>
    <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportToExcel}>
      Export to Excel
    </Button>

    <Button
      variant="contained"
      color="success"
      startIcon={<AddIcon />}
      onClick={() => {
        setModalType("in");
        setOpenModal(true);
      }}
    >
      Cash In
    </Button>

    <Button
      variant="contained"
      color="error"
      startIcon={<RemoveIcon />}
      onClick={() => {
        setModalType("out");
        setOpenModal(true);
      }}
    >
      Cash Out
    </Button>
  </Box>
</Box>
      {/* Table */}
      <Paper sx={{ borderRadius: 4, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
              <TableCell>Ref No</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Remark</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEntries?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEntries.map((entry, i) => (
                  <Zoom in key={i}>
                    <TableRow>
                        <TableCell>Ref_{entry.id}</TableCell>
                      <TableCell>{new Date(entry.tranjectiondate).toLocaleString('en-GB')}</TableCell>
                      <TableCell sx={{ color: entry.type === "CR" ? "green" : "red", fontWeight: "bold" }}>
                        {entry.type}
                      </TableCell>
                      <TableCell>{Companycurrency}{entry.amount.toFixed(2)}</TableCell>
                      <TableCell>{entry.remark}</TableCell>
                    </TableRow>
                  </Zoom>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle>{modalType === "in" ? "💸 Add Cash In" : "💵 Add Cash Out"}</DialogTitle>
        <DialogContent>
          <TextField label="Amount" fullWidth type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <TextField label="Remark" fullWidth value={remark} onChange={(e) => setRemark(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
